import {Card, CardBody, Form, NumberInput, SelectItem} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {TableBody, TableCell, TableColumn, TableHeader, TableRow} from "@heroui/table";
import {addToast} from "@heroui/toast";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {createFileRoute, useNavigate, useParams} from '@tanstack/react-router'
import React from "react";
import Content from "../../../components/Content.tsx";
import PButton from "../../../components/PButton.tsx";
import PInput from "../../../components/PInput.tsx";
import PSelect from "../../../components/PSelect.tsx";
import PTable from "../../../components/PTable.tsx";
import Header from "../../../Header.tsx";
import {useJwtAuth} from "../../../JWTAuthProvider.tsx";
import {tableFullScreen} from "../../../components/tableStyles.ts";
import services from "../../../services";
import {FirebaseMessage} from "../../../types";
import queryKeys from "../../../utilities/queryKeys.ts";
import useIsSm from "../../../utilities/useIsSm.ts";

export const Route = createFileRoute('/_app/master/push-notifications')({
    component: RouteComponent,
})

function RouteComponent() {
    const jwt = useJwtAuth();
    const user = jwt.user;
    const params = useParams({strict: false});
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const messagesQuery = useQuery<FirebaseMessage[]>({
        queryKey: queryKeys.firebase.messages(),
        queryFn:  services.firebase.messages,
    })

    const sendTestMessage = useMutation({
        mutationFn: services.firebase.sendTestMessage,
        onSuccess:  () => {
            addToast({color: "success", title: "Message sent!"});
            queryClient.invalidateQueries({queryKey: queryKeys.firebase.messages()})
        },
        onError(error) {
            addToast({color: "danger", title: error?.message || "Something went wrong."});
        }
    })

    const submitTestForm = (e) => {
        e.preventDefault();
        const data: any = Object.fromEntries(new FormData(e.currentTarget));

        sendTestMessage.mutate(data, {
            onSuccess: () => {
                e.target?.reset();
            }
        })
    }

    const isSm = useIsSm();

    const columns = (isSm
            ? [
                {key: 'combined', title: 'Message'},
                {key: 'actions', title: ''},
            ]
            : [
                {key: 'created_at', title: 'Date'},
                {key: 'target', title: 'Target'},
                {key: 'content', title: 'Content'},
                {key: 'actions', title: ''},
            ]
    );

    if (!user) {
        return <div>
            <Header />

            <Content>Please login.</Content>
        </div>;
    }

    if (messagesQuery.isLoading) {
        return <div className="flex justify-center my-6">
            <Spinner size={"lg"} />
        </div>
    }

    return (
        <>
            <Header />
            <Content>
                <Card className={"mb-4"}>
                    <CardBody>

                        <Form onSubmit={submitTestForm}>
                            <PInput
                                isRequired={true}
                                label={"Title"}
                                name={"title"}
                                defaultValue={"League results updated!"}
                            ></PInput>
                            <PInput
                                isRequired={true}
                                label={"Body"}
                                name={"body"}
                                defaultValue={"Check out the latest results!"}
                            ></PInput>
                            <NumberInput
                                label={"Badge"}
                                name={"badge"}
                                labelPlacement={"outside"}
                                defaultValue={1}
                            ></NumberInput>
                            <PInput
                                label={"URL"}
                                name={"navigate_to"}
                                defaultValue={"/leagues/"}></PInput>
                            <PSelect name={"topic"} label={"Target"} defaultSelectedKeys={["all"]}>
                                <SelectItem key="all" title="All Devices"></SelectItem>
                                <SelectItem key="android" title="Android Devices"></SelectItem>
                                <SelectItem key="ios" title="iOS Devices"></SelectItem>
                            </PSelect>
                            <PInput
                                label={"Target user token"}
                                name={"user_token"}
                                defaultValue={""}
                            ></PInput>

                            <PButton type={"submit"}>Send</PButton>
                        </Form>
                    </CardBody>
                </Card>

                <PTable removeWrapper={false} classNames={tableFullScreen} isStriped={true} aria-label={"Club Players"}>
                    <TableHeader columns={columns}>
                        {(column) => <TableColumn key={column.key}>{column.title}</TableColumn>}
                    </TableHeader>
                    <TableBody>
                        {(messagesQuery.data || []).map((msg: FirebaseMessage) => <TableRow key={msg.uid}>
                            {(columnKey) => {
                                if (columnKey === 'combined') {
                                    return <TableCell>
                                        <div className="">
                                            {msg.created_at}<br />
                                            <strong>{msg.meta.notification?.title}</strong><br />
                                            {msg.meta.notification?.body}<br />
                                            URL: {msg.meta.data?.['navigate-to']}<br />
                                            {msg.meta.topic &&
                                                <>To {msg.meta.topic} devices.<br /></>
                                            }
                                            {msg.meta.token &&
                                                <>User: {msg.meta.token?.substring(0, 10)}...</>
                                            }
                                            {msg.meta._target_count &&
                                                <>To {msg.meta._target_count} player(s)</>
                                            }
                                        </div>
                                    </TableCell>
                                }
                                if (columnKey === 'target') {
                                    return <TableCell className={""}>
                                        {msg.meta.topic &&
                                            <>Devices: {msg.meta.topic}<br /></>
                                        }
                                        {msg.meta.token &&
                                            <>User: {msg.meta.token?.substring(0, 10)}...</>
                                        }
                                        {msg.meta._target_count &&
                                            <>{msg.meta._target_count} player(s)</>
                                        }
                                    </TableCell>
                                }
                                if (columnKey === 'content') {
                                    return <TableCell className={""}>
                                        <strong>{msg.meta.notification?.title}</strong><br />
                                        {msg.meta.notification?.body}<br />
                                        URL: {msg.meta.data?.['navigate-to']}
                                    </TableCell>
                                }
                                if (columnKey === 'actions') {
                                    return <TableCell className={"flex justify-end gap-2"}>
                                        {' '}
                                    </TableCell>
                                }
                                if (msg.hasOwnProperty(columnKey)) {
                                    return <TableCell>
                                        {msg[columnKey] || '-'}
                                    </TableCell>
                                }
                                if (msg.meta?.hasOwnProperty(columnKey)) {
                                    return <TableCell>
                                        {msg.meta[columnKey] || '-'}
                                    </TableCell>
                                }
                                return (
                                    <TableCell>
                                        {columnKey}
                                    </TableCell>
                                );
                            }}
                        </TableRow>)}
                    </TableBody>

                </PTable>

            </Content>
        </>
    )
}